import { useCallback, useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import type { MapRef } from 'react-map-gl/maplibre';
import type maplibregl from 'maplibre-gl';

import { ORBIT_LAYER_IDS } from '@/components/map/utils/mapLayerOrder';
import { pickTopEntityFeature } from '@/components/map/utils/entityHitTest';
import type { PoiFeatureProperties } from '@/components/map/utils/poiFeatureProperties';
import { toLngLatTuple } from '@/utils/mapUtils';

// Hit-testing spans both entity families. Leak markers render above POI pins, and
// queryRenderedFeatures returns rendered features top-first by render order, so the
// single-winner rule (pickTopEntityFeature) resolves cross-family overlaps by layer order
// alone. The list is filtered to layers that exist so the hook works identically before
// the leak layer is registered.
const ENTITY_HIT_LAYERS = [ORBIT_LAYER_IDS.leaks, ORBIT_LAYER_IDS.pois] as const;

function queryEntityFeatures(mlMap: maplibregl.Map, point: maplibregl.PointLike) {
    const layers = ENTITY_HIT_LAYERS.filter((id) => !!mlMap.getLayer(id));
    if (layers.length === 0) return [];
    return mlMap.queryRenderedFeatures(point, { layers });
}

/**
 * Click outcomes are reported through callbacks rather than owned as state: POI
 * selection lives in the map-interaction provider, and this hook's job is the
 * MapLibre listener plumbing plus hover state only.
 */
export interface PoiInteractionCallbacks {
    /** A POI feature was clicked. `coords` is the feature's Point [lng, lat] when available. */
    onPoiClick?: (poi: PoiFeatureProperties, coords: [number, number] | null) => void;
}

export interface PoiInteractionState {
    hoveredPoi: PoiFeatureProperties | null;
    /** Geographic [lng, lat] of the hovered POI — the MapLibre `<Popup>` anchor. */
    hoveredPoiLngLat: [number, number] | null;
    onHoverCardEnter: () => void;
    onHoverCardLeave: () => void;
}

/**
 * Manages hover state and click reporting for individual POI features on the map.
 *
 * Uses `mousemove` (not `mouseenter`) for hover detection so `queryRenderedFeatures`
 * can reliably return the topmost feature at the cursor when POIs overlap.
 * A ref-based id guard prevents React state updates when the cursor stays
 * over the same feature.
 *
 * The hovered POI's geographic point anchors the PoiPopup; the MapLibre `<Popup>`
 * projects and tracks it on pan/zoom itself, so no screen-pixel bookkeeping is needed.
 *
 * Empty-map clicks are not this hook's concern: the single global click listener
 * (useMapClickDismissal) decides emptiness for the whole map, so a POI click can never
 * be mistaken for an empty click by another handler with a narrower view of the layers.
 */
export function usePoiInteractions(
    mapRef: RefObject<MapRef | null>,
    isMapLoaded: boolean,
    callbacks?: PoiInteractionCallbacks,
    enabled: boolean = true,
): PoiInteractionState {
    const [hoveredPoi, setHoveredPoi] = useState<PoiFeatureProperties | null>(null);
    const [hoveredPoiLngLat, setHoveredPoiLngLat] = useState<[number, number] | null>(null);

    const hoveredIdRef = useRef<string | null>(null);
    const leaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Holds the current dismiss function so stable callbacks below can call it
    // without capturing a stale closure.
    const dismissHoverRef = useRef<() => void>(() => {});
    // Ref-held so the delegated listeners (registered once) always call the latest
    // render's callbacks without re-registering.
    const callbacksRef = useRef(callbacks);
    useEffect(() => {
        callbacksRef.current = callbacks;
    });

    // Stable callbacks passed to PoiPopup so it can cancel/trigger dismissal.
    const onHoverCardEnter = useCallback(() => {
        if (leaveTimeoutRef.current !== null) {
            clearTimeout(leaveTimeoutRef.current);
            leaveTimeoutRef.current = null;
        }
    }, []);

    const onHoverCardLeave = useCallback(() => {
        dismissHoverRef.current();
    }, []);

    useEffect(() => {
        if (!enabled || !isMapLoaded) return;
        const mlMap = mapRef.current?.getMap();
        if (!mlMap) return;

        // Kept up-to-date so the stable onHoverCardLeave callback can call it.
        dismissHoverRef.current = () => {
            leaveTimeoutRef.current = null;
            hoveredIdRef.current = null;
            mlMap.getCanvas().style.cursor = '';
            setHoveredPoi(null);
            setHoveredPoiLngLat(null);
        };

        const handlePoiMouseMove = (e: maplibregl.MapLayerMouseEvent) => {
            // No hover while the camera moves — a card created mid-drag/zoom/fly would
            // pan with the map, anchored to a point sliding out from under the cursor.
            if (mlMap.isMoving()) return;
            // Cancel any pending dismiss when the cursor re-enters a POI.
            if (leaveTimeoutRef.current !== null) {
                clearTimeout(leaveTimeoutRef.current);
                leaveTimeoutRef.current = null;
            }

            const winner = pickTopEntityFeature(queryEntityFeatures(mlMap, e.point));
            if (!winner) return;
            const props = winner.properties as PoiFeatureProperties;

            // No state update when the cursor has not moved to a different feature.
            if (props.id === hoveredIdRef.current) return;

            hoveredIdRef.current = props.id;
            const geometry = winner.geometry;
            const lngLat: [number, number] =
                geometry.type === 'Point' ? (geometry.coordinates as [number, number]) : [e.lngLat.lng, e.lngLat.lat];

            setHoveredPoi(props);
            setHoveredPoiLngLat(lngLat);
        };

        // Defers clearing hover state by 150 ms so the cursor has time to reach
        // the hover card without the card disappearing mid-travel.
        const handlePoiMouseLeave = () => {
            leaveTimeoutRef.current = setTimeout(() => {
                dismissHoverRef.current();
            }, 150);
        };

        const handlePoiClick = (e: maplibregl.MapLayerMouseEvent) => {
            const winner = pickTopEntityFeature(queryEntityFeatures(mlMap, e.point));
            if (!winner) return;
            const props = winner.properties as PoiFeatureProperties;

            // Dismiss hover before the click opens the details panel.
            hoveredIdRef.current = null;
            setHoveredPoi(null);
            setHoveredPoiLngLat(null);
            const coords =
                winner.geometry.type === 'Point' ? (toLngLatTuple(winner.geometry.coordinates) ?? null) : null;
            callbacksRef.current?.onPoiClick?.(props, coords);
        };

        // Any camera movement (drag, zoom, fly-to, ease-to) dismisses the hover card
        // immediately — cancelling the deferred leave dismissal first so it cannot fire
        // late. The isMoving() guard above keeps hover suppressed until movement ends.
        const handleMoveStart = () => {
            if (leaveTimeoutRef.current !== null) {
                clearTimeout(leaveTimeoutRef.current);
            }
            dismissHoverRef.current();
        };

        // Registered once — delegated layer listeners re-resolve their layer ids on every
        // event, so they survive a style reload. Re-registering them on `style.load` would
        // duplicate them: MapLibre dedupes plain listeners but not delegated ones.
        for (const layerId of ENTITY_HIT_LAYERS) {
            mlMap.on('mousemove', layerId, handlePoiMouseMove);
            mlMap.on('mouseleave', layerId, handlePoiMouseLeave);
            mlMap.on('click', layerId, handlePoiClick);
        }
        mlMap.on('movestart', handleMoveStart);

        return () => {
            if (leaveTimeoutRef.current !== null) {
                clearTimeout(leaveTimeoutRef.current);
                leaveTimeoutRef.current = null;
            }
            for (const layerId of ENTITY_HIT_LAYERS) {
                mlMap.off('mousemove', layerId, handlePoiMouseMove);
                mlMap.off('mouseleave', layerId, handlePoiMouseLeave);
                mlMap.off('click', layerId, handlePoiClick);
            }
            mlMap.off('movestart', handleMoveStart);
            // Reset cursor in case the component unmounts while a POI is hovered.
            mlMap.getCanvas().style.cursor = '';
        };
    }, [enabled, isMapLoaded, mapRef]);

    return {
        hoveredPoi,
        hoveredPoiLngLat,
        onHoverCardEnter,
        onHoverCardLeave,
    };
}
